import { date, number, object, string, ValidationError } from 'yup';
import {
  parseDate,
  transformBoolean,
  transformNumber,
  transformString,
} from './schema.helpers';
import dayjs from 'dayjs';

export const booleanNumberSchema = number()
  .transform(transformBoolean)
  .oneOf([0, 1])
  .default(0);

export const positiveNumber = number().transform(transformNumber).min(0);
export const positiveInteger = positiveNumber.round('round');
export const testDate = date()
  .label('Test Date')
  .transform(parseDate)
  .required()
  .test((value) => {
    if (value && dayjs(value).isAfter(dayjs().endOf('day'))) {
      return new ValidationError('Test date cannot be in the future');
    }
    return true;
  });

export const timestamp = positiveInteger;

// 3 digits, no decimal. Range should fall between >=30<=170, provide warning for values <=40 or >=150
export const systemPsi = positiveInteger.label('System PSI').required();
// .max(170);

export const baseTestAttemptSchema = {
  testDate,
  timestamp,
  systemPsi,
};

export const repairSchema = object({
  testDate,
  timestamp,
  comment: string()
    .label('Repair Comment')
    .transform(transformString)
    .required(),
}).label('Repair');

// TODO: update this to match Paul's new version of this form with check1Apparent and failingComponents
export const rpTestAttemptSchema = object({
  ...baseTestAttemptSchema,
  reliefValveDripping: string()
    // TODO: might be deprecated in favor of Paul's new "Failing Component(s)" list
    .label('Relief Valve Discharging')
    .transform(transformString)
    .oneOf(['no', 'upon_arrival', 'upon_closure'])
    .required(),

  // when water supplier rpCheck2Type is USC10, check2 can only be 'tight' or 'leaked'
  // when water supplier rpCheck2Type is VALUE, check2 is automatically 'other', require check2Other and it has to be 1.0 or greater to pass
  // when water supplier rpCheck2Type is VALUE_BUFFER, check2 is automatically 'other', require check2Other and adjust test result status to include value buffer
  check2: string()
    .label('Check 2')
    .transform(transformString)
    .oneOf(['tight', 'leaked', 'other'])
    .when('reliefValveDripping', ([reliefValveDripping], schema) => {
      if (!reliefValveDripping || reliefValveDripping === 'no') {
        return schema.required();
      }
      return schema;
    }),
  check2Other: positiveNumber
    .label('Check 2 Other')
    .when('check2', ([check2], schema) => {
      if (check2 === 'other') {
        return schema.required();
      }
      return schema;
    }),
  pressureDrop: positiveNumber // TODO: rename to check1 or check1Actual?
    .label('Check 1 Pressure Drop')
    .when('reliefValveDripping', ([reliefValveDripping], schema) => {
      if (
        !reliefValveDripping ||
        reliefValveDripping === 'no' ||
        reliefValveDripping === 'upon_closure'
      ) {
        return schema.required();
      }
      return schema;
    }),
  didNotOpen: booleanNumberSchema.label('Relief Valve Did Not Open'),
  // Provide warning if value >5.0, If value <1.0, the test fails
  openedAt: positiveNumber
    .label('Relief Valve Opened At')
    .when(
      ['didNotOpen', 'reliefValveDripping'],
      ([didNotOpen, reliefValveDripping], schema) => {
        if (
          !didNotOpen &&
          (!reliefValveDripping ||
            reliefValveDripping === 'no' ||
            reliefValveDripping === 'upon_closure')
        ) {
          return schema.required();
        }
        return schema;
      }
    ),
}).label('RP');

export const dcTestAttemptSchema = object({
  ...baseTestAttemptSchema,
  // Provide warning if value >5.0, If value <1.0, the test fails
  check1: positiveNumber.label('Check 1').required(),
  check2: positiveNumber.label('Check 2').required(),
}).label('DC');

export const svPvTestAttemptSchema = object({
  ...baseTestAttemptSchema,
  didNotOpen: booleanNumberSchema.label('Did Not Fully Open'),
  openedAt: positiveNumber.label('Air Inlet Opened At').when('didNotOpen', {
    is: 1,
    otherwise: (openedAt) => openedAt.required(),
  }),
  pressureDrop: positiveNumber.label('Check Valve').required(),
}).label('SV/PV');

export const type2BypassTestAttemptSchema = object({
  testDate,
  timestamp,
  bypassCheck: positiveNumber.label('Bypass Check').required(),
}).label('Type 2 Bypass');

export const avbAirGapTestAttemptSchema = object({
  testDate,
  timestamp,
}).label('AVB/Air Gap');
